import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Header from '@/components/ui/Header';
import Button from '@/components/ui/Button';
import Icon from '@/components/AppIcon';
import LoadingState from '@/components/ui/LoadingState';
import { useAuth } from '@/context/AuthContext';
import { useLocations } from '@/context/LocationsContext';
import useTranslation from '@/hooks/useTranslation';
import formatTranslation from '@/utils/formatTranslation';
import { createJobApplication, fetchJobApplications, updateJobApplicationStatus } from '@/lib/api/jobApplications';
import CreateApplicationModal from './components/CreateApplicationModal';

const LANGUAGE_TO_LOCALE = {
  en: 'en-US',
  de: 'de-DE',
  fr: 'fr-FR',
};

const APPLICATION_STATUSES = {
  screening: {
    labelKey: 'hiring.statuses.screening',
    className: 'bg-muted/40 text-muted-foreground border border-border',
  },
  'interview-coming-up': {
    labelKey: 'hiring.statuses.interview-coming-up',
    className: 'bg-primary/10 text-primary border border-primary/30',
  },
  interviewed: {
    labelKey: 'hiring.statuses.interviewed',
    className: 'bg-secondary/10 text-secondary-foreground border border-secondary/30',
  },
  hired: {
    labelKey: 'hiring.statuses.hired',
    className: 'bg-success/10 text-success border border-success/30',
  },
  declined: {
    labelKey: 'hiring.statuses.declined',
    className: 'bg-error/10 text-error border border-error/30',
  },
};

const STATUS_FILTERS = [
  { value: 'all', labelKey: 'hiring.filters.all' },
  { value: 'screening', labelKey: 'hiring.filters.screening' },
  { value: 'interview-coming-up', labelKey: 'hiring.filters.interview-coming-up' },
  { value: 'interviewed', labelKey: 'hiring.filters.interviewed' },
  { value: 'hired', labelKey: 'hiring.filters.hired' },
  { value: 'declined', labelKey: 'hiring.filters.declined' },
];

const STATUS_PIPELINE = ['screening', 'interview-coming-up', 'interviewed', 'hired'];

const formatDate = (isoString, locale = 'en-US', fallback = '—') => {
  if (!isoString) {
    return fallback;
  }

  const date = new Date(isoString);
  if (Number.isNaN(date.getTime())) {
    return fallback;
  }

  return date.toLocaleDateString(locale, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

const isFutureDate = (value) => {
  if (!value) {
    return false;
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return false;
  }

  return date.getTime() > Date.now();
};

const HiringPage = () => {
  const { t, language } = useTranslation(['header', 'common', 'hiring']);
  const { user } = useAuth();
  const { locations, loading: locationsLoading } = useLocations();

  const [selectedLocation, setSelectedLocation] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [updatingId, setUpdatingId] = useState(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isSubmittingNew, setIsSubmittingNew] = useState(false);

  const userRole = user?.role ?? 'manager';

  const locationMap = useMemo(() => {
    const map = new Map();
    if (Array.isArray(locations)) {
      locations.forEach((location) => {
        if (location?.id) {
          map.set(location.id, location);
        }
      });
    }
    return map;
  }, [locations]);

  useEffect(() => {
    if (!selectedLocation && Array.isArray(locations) && locations.length > 0) {
      setSelectedLocation(locations[0]);
    }
  }, [locations, selectedLocation]);

  const locale = LANGUAGE_TO_LOCALE[language] ?? LANGUAGE_TO_LOCALE.en ?? 'en-US';
  const datePlaceholder = t('hiring.defaults.datePlaceholder', '—');

  const formatAppliedDate = useCallback(
    (isoString) => formatDate(isoString, locale, datePlaceholder),
    [datePlaceholder, locale],
  );

  const loadApplications = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchJobApplications(
        selectedLocation?.id ? { locationId: selectedLocation.id } : {},
      );
      setApplications(Array.isArray(data) ? data : []);
      setError('');
    } catch (err) {
      setError(
        t(
          'hiring.applications.loadError',
          'Unable to load applications right now. Please try again shortly.',
        ),
      );
      setApplications([]);
    } finally {
      setLoading(false);
    }
  }, [selectedLocation?.id, t]);

  useEffect(() => {
    loadApplications();
  }, [loadApplications, selectedLocation?.id]);

  const localizedStatuses = useMemo(
    () =>
      Object.entries(APPLICATION_STATUSES).reduce(
        (acc, [key, config]) => ({
          ...acc,
          [key]: { ...config, label: t(config.labelKey) },
        }),
        {},
      ),
    [t],
  );

  const statusFilterOptions = useMemo(
    () =>
      STATUS_FILTERS.map((filter) => ({
        ...filter,
        label: t(filter.labelKey),
      })),
    [t],
  );

  const statusSummary = useMemo(() => {
    const upcomingInterviews = applications.filter((application) =>
      isFutureDate(application?.interviewDate),
    ).length;
    const hired = applications.filter((application) => application?.status === 'hired').length;
    const declined = applications.filter((application) => application?.status === 'declined').length;

    return {
      total: applications.length,
      upcomingInterviews,
      hired,
      declined,
    };
  }, [applications]);

  const normalizedApplications = useMemo(
    () =>
      applications.map((application) => ({
        ...application,
        locationLabel:
          application?.locationName ||
          locationMap.get(application?.locationId)?.name ||
          application?.locationId ||
          t('hiring.defaults.locationPlaceholder', 'Location not specified'),
        positionLabel:
          application?.positionTitle ||
          application?.positionId ||
          t('hiring.defaults.positionPlaceholder', 'Position not specified'),
        metadata: application?.metadata ?? {},
      })),
    [applications, locationMap, t],
  );

  const filteredApplications = useMemo(() => {
    const matchesStatus = (application) =>
      statusFilter === 'all' || application?.status === statusFilter;

    const selectedLocationId = selectedLocation?.id;
    const hasLocationMatch = selectedLocationId
      ? applications.some((application) => application?.locationId === selectedLocationId)
      : false;

    const matchesLocation = (application) => {
      if (!selectedLocationId || !hasLocationMatch) {
        return true;
      }

      return application?.locationId === selectedLocationId;
    };

    return normalizedApplications.filter(
      (application) => matchesStatus(application) && matchesLocation(application),
    );
  }, [applications, normalizedApplications, selectedLocation, statusFilter]);

  const getNextStatus = useCallback((status) => {
    if (!status || status === 'declined' || status === 'hired') {
      return null;
    }

    const currentIndex = STATUS_PIPELINE.indexOf(status);
    if (currentIndex === -1) {
      return STATUS_PIPELINE[0];
    }

    return STATUS_PIPELINE[currentIndex + 1] ?? null;
  }, []);

  const updateStatus = useCallback(
    async (application, nextStatus) => {
      if (!application?.id || !nextStatus) {
        return;
      }
      setUpdatingId(application.id);
      try {
        const updated = await updateJobApplicationStatus(application.id, nextStatus);
        setApplications((prev) =>
          prev.map((item) => (item.id === application.id ? { ...item, ...updated } : item)),
        );
        setError('');
      } catch (err) {
        setError(
          t(
            'hiring.applications.statusUpdateError',
            'Could not update application status. Please try again.',
          ),
        );
      } finally {
        setUpdatingId(null);
      }
    },
    [t],
  );

  const handleAdvanceStatus = useCallback(
    (application) => {
      const nextStatus = getNextStatus(application?.status);
      if (!nextStatus) {
        return;
      }
      updateStatus(application, nextStatus);
    },
    [getNextStatus, updateStatus],
  );

  const handleCreateApplication = useCallback(async (payload) => {
    if (!payload?.locationId || !payload?.positionId) {
      return;
    }

    setIsSubmittingNew(true);
    setError('');

    const selectedLocationRecord = locations?.find?.((location) => location?.id === payload.locationId);

    try {
      const created = await createJobApplication({
        ...payload,
        locationName: selectedLocationRecord?.name ?? payload.locationId,
      });
      setApplications((prev) => [created, ...prev]);
      setIsCreateModalOpen(false);
    } catch (err) {
      const message =
        err?.response?.data?.message
        ?? err?.message
        ?? t('hiring.modal.create.errors.submitFailed', 'Could not save the application.');
      setError(message);
      throw err;
    } finally {
      setIsSubmittingNew(false);
    }
  }, [locations, t]);

  const handleDecline = useCallback(
    (application) => {
      if (!application?.id || application?.status === 'declined') {
        return;
      }
      updateStatus(application, 'declined');
    },
    [updateStatus],
  );

  const renderStatusPill = (application) => {
    const statusConfig = localizedStatuses[application?.status] ?? localizedStatuses.screening;
    return (
      <span
        className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-medium ${statusConfig.className}`}
      >
        {statusConfig.label}
      </span>
    );
  };

  const renderActions = (application) => {
    const nextStatus = getNextStatus(application?.status);
    const isUpdating = updatingId === application?.id;

    return (
      <div className="flex flex-wrap gap-2">
        <Button
          size="sm"
          variant="default"
          disabled={!nextStatus || isUpdating}
          onClick={() => handleAdvanceStatus(application)}
        >
          {nextStatus ? t('hiring.applications.actions.advance') : t('hiring.statuses.hired')}
        </Button>
        {application?.status !== 'declined' && application?.status !== 'hired' ? (
          <Button
            size="sm"
            variant="outline"
            disabled={isUpdating}
            onClick={() => handleDecline(application)}
          >
            {t('hiring.applications.actions.decline')}
          </Button>
        ) : null}
      </div>
    );
  };

  const renderMetadata = (metadata = {}) => {
    const entries = Object.entries(metadata ?? {}).filter(([, value]) => value !== null && value !== undefined);

    if (entries.length === 0) {
      return <span className="text-sm text-muted-foreground">{t('hiring.defaults.datePlaceholder', '—')}</span>;
    }

    return (
      <div className="flex flex-wrap gap-2">
        {entries.map(([key, value]) => (
          <span
            key={key}
            className="inline-flex items-center rounded-full bg-muted/40 px-2.5 py-1 text-xs font-medium text-muted-foreground"
          >
            {key}: {String(value)}
          </span>
        ))}
      </div>
    );
  };

  const renderErrorBanner = () => {
    if (!error) {
      return null;
    }

    return (
      <div className="rounded-lg border border-error/30 bg-error/10 text-error px-4 py-3 text-sm">
        {error}
      </div>
    );
  };

  const isInitialLoading = loading && applications.length === 0;
  const isRefreshing = loading && applications.length > 0;
  const isSummaryLoading = loading && applications.length === 0;

  return (
    <div className="min-h-screen bg-background">
      <Header
        userRole={userRole}
        selectedLocation={
          selectedLocation?.name ?? (locationsLoading ? t('header.location.loadingLocations') : '')
        }
        locations={locations}
        onLocationChange={setSelectedLocation}
        allowLocationSelection
        notifications={{ messages: 0, timeOff: 0 }}
      />

      <main className="pt-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8">
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div>
              <h1 className="text-3xl font-bold text-foreground">{t('hiring.page.title')}</h1>
              <p className="text-muted-foreground mt-2 max-w-2xl">{t('hiring.page.description')}</p>
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                iconName="RefreshCcw"
                onClick={loadApplications}
                disabled={loading}
              >
                {t('hiring.applications.actions.refresh', 'Refresh')}
              </Button>
              <Button
                variant="default"
                iconName="Plus"
                onClick={() => setIsCreateModalOpen(true)}
              >
                {t('hiring.page.cta')}
              </Button>
            </div>
          </div>

          {renderErrorBanner()}

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {[ 
              {
                label: t('hiring.stats.total'),
                value: statusSummary.total,
                icon: 'Users',
                accent: 'bg-primary/10 text-primary',
              },
              {
                label: t('hiring.stats.upcomingInterviews'),
                value: statusSummary.upcomingInterviews,
                icon: 'Calendar',
                accent: 'bg-secondary/10 text-secondary-foreground',
              },
              {
                label: t('hiring.stats.hired'),
                value: statusSummary.hired,
                icon: 'BadgeCheck',
                accent: 'bg-success/10 text-success',
              },
              {
                label: t('hiring.stats.declined'),
                value: statusSummary.declined,
                icon: 'ThumbsDown',
                accent: 'bg-error/10 text-error',
              },
            ].map((stat) => (
              <div key={stat.label} className="bg-card border border-border rounded-xl shadow-elevation-1 p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs uppercase tracking-wide text-muted-foreground">{stat.label}</p>
                    {isSummaryLoading ? (
                      <div className="mt-2 h-7 w-16 rounded bg-muted/50 animate-pulse" />
                    ) : (
                      <p className="text-2xl font-semibold text-foreground mt-2">{stat.value}</p>
                    )}
                  </div>
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${stat.accent}`}>
                    <Icon name={stat.icon} size={20} />
                  </div>
                </div>
              </div>
            ))}
          </div>

          <section className="bg-card border border-border rounded-xl shadow-elevation-1">
            <div className="flex flex-col gap-4 border-b border-border p-6 md:flex-row md:items-center md:justify-between">
              <div>
                <h2 className="text-xl font-semibold text-foreground">{t('hiring.applications.sectionTitle')}</h2>
                <p className="text-sm text-muted-foreground">
                  {formatTranslation(t('hiring.applications.summary'), {
                    current: filteredApplications.length,
                    total: applications.length,
                  })}
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                {statusFilterOptions.map((filter) => (
                  <Button
                    key={filter.value}
                    size="sm"
                    variant={statusFilter === filter.value ? 'default' : 'outline'}
                    onClick={() => setStatusFilter(filter.value)}
                  >
                    {filter.label}
                  </Button>
                ))}
              </div>
            </div>

            {isInitialLoading ? (
              <div className="p-10">
                <LoadingState message={t('hiring.applications.loading', 'Loading applications...')} />
              </div>
            ) : (
              <>
                {isRefreshing ? (
                  <div className="flex items-center gap-2 border-b border-border px-6 py-3 text-sm text-muted-foreground">
                    <Icon name="Loader2" size={16} className="animate-spin" />
                    <span>{t('hiring.applications.loading', 'Loading applications...')}</span>
                  </div>
                ) : null}
                <div className="hidden lg:block">
                  <table className="w-full">
                    <thead className="bg-muted/30 border-b border-border">
                      <tr>
                        <th className="px-6 py-4 text-left text-sm font-medium text-muted-foreground">
                          {t('hiring.applications.table.applicant')}
                        </th>
                        <th className="px-6 py-4 text-left text-sm font-medium text-muted-foreground">
                          {t('hiring.applications.table.position')}
                        </th>
                        <th className="px-6 py-4 text-left text-sm font-medium text-muted-foreground">
                          {t('hiring.applications.table.location')}
                        </th>
                        <th className="px-6 py-4 text-left text-sm font-medium text-muted-foreground">
                          {t('hiring.applications.table.appliedDate')}
                        </th>
                        <th className="px-6 py-4 text-left text-sm font-medium text-muted-foreground">
                          {t('hiring.applications.table.metadata', 'Metadata')}
                        </th>
                        <th className="px-6 py-4 text-left text-sm font-medium text-muted-foreground">
                          {t('hiring.applications.table.status')}
                        </th>
                        <th className="px-6 py-4 text-left text-sm font-medium text-muted-foreground">
                          {t('hiring.applications.table.actions', 'Actions')}
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {filteredApplications.length === 0 ? (
                        <tr>
                          <td colSpan={7} className="px-6 py-10 text-center text-sm text-muted-foreground">
                            {t('hiring.applications.empty')}
                          </td>
                        </tr>
                      ) : (
                        filteredApplications.map((application) => (
                          <tr key={application?.id ?? application?.applicationNumber} className="hover:bg-muted/20">
                            <td className="px-6 py-4">
                              <div className="flex flex-col">
                                <span className="text-sm font-semibold text-foreground">
                                  {application?.fullName ?? '—'}
                                </span>
                                <span className="text-xs text-muted-foreground">
                                  {application?.applicationNumber ?? '—'}
                                </span>
                              </div>
                            </td>
                            <td className="px-6 py-4 text-sm text-foreground">{application?.positionLabel ?? '—'}</td>
                            <td className="px-6 py-4 text-sm text-foreground">{application?.locationLabel ?? '—'}</td>
                          <td className="px-6 py-4 text-sm text-foreground">
                            {formatAppliedDate(application?.appliedAt)}
                          </td>
                          <td className="px-6 py-4">{renderMetadata(application?.metadata)}</td>
                          <td className="px-6 py-4">{renderStatusPill(application)}</td>
                          <td className="px-6 py-4">{renderActions(application)}</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>

                <div className="lg:hidden divide-y divide-border border-t border-border">
                  {filteredApplications.length === 0 ? (
                    <div className="p-6 text-center text-sm text-muted-foreground">
                      {t('hiring.applications.empty')}
                    </div>
                  ) : (
                    filteredApplications.map((application) => (
                      <div key={application?.id ?? application?.applicationNumber} className="p-4 space-y-3">
                        <div className="flex items-start justify-between gap-3">
                          <div>
                            <p className="text-sm font-semibold text-foreground">{application?.fullName ?? '—'}</p>
                            <p className="text-xs text-muted-foreground">{application?.positionLabel ?? '—'}</p>
                          </div>
                          {renderStatusPill(application)}
                        </div>
                        <div className="grid grid-cols-1 gap-2 text-sm text-muted-foreground">
                          <p>
                            <span className="font-medium text-foreground">{t('hiring.applications.mobile.location')}</span>{' '}
                            {application?.locationLabel ?? '—'}
                          </p>
                          <p>
                            <span className="font-medium text-foreground">{t('hiring.applications.mobile.applied')}</span>{' '}
                            {formatAppliedDate(application?.appliedAt)}
                          </p>
                          {application?.metadata ? (
                            <div>
                              <span className="font-medium text-foreground">{t('hiring.applications.table.metadata', 'Metadata')}:</span>{' '}
                              <div className="mt-2">{renderMetadata(application.metadata)}</div>
                            </div>
                          ) : null}
                          {application?.experienceSummary ? (
                            <p>
                              <span className="font-medium text-foreground">{t('hiring.applications.mobile.notes')}</span>{' '}
                              {application.experienceSummary}
                            </p>
                          ) : null}
                        </div>
                        {renderActions(application)}
                      </div>
                    ))
                  )}
                </div>
              </>
            )}
          </section>
        </div>
      </main>

      <CreateApplicationModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSubmit={handleCreateApplication}
        locations={locations}
        isSubmitting={isSubmittingNew}
      />
    </div>
  );
};

export default HiringPage;
